import { Vector3 } from 'three/webgpu';
import type { BiomeField } from '../../engine/terrain/BiomeField';
import type { TerrainProfile } from '../../engine/terrain/TerrainProfile';
import { TerrainHeights } from '../../engine/terrain/terrainHeight';
import type { BuiltUniverse } from './build';
import { BIOMES } from './catalog';
import type { UniverseSpec, WorldSpec } from './spec';

/*
 * The editor's previews, drawn on the CPU from the same heights and biome weights the game
 * will generate: a rough map of one world, and the whole universe seen from outside, with its
 * caves.
 */

type RGB = [number, number, number];

export interface MapStats {
  /** Share of the surface under water (or lava). */
  water: number;
  /** Highest ground (m above sea level). */
  peak: number;
  /** Share of the land each biome has (by weight). */
  biomes: number[];
}

/**
 * Draw an equirectangular map of a world into `canvas`, a few rows at a time so the page stays
 * responsive: each biome's colours where it is, water or lava, hill shading, and dots at
 * `marks` (unit directions: cave mouths, the start). Resolves with the map's stats, or null if
 * `cancelled()` turned true before it finished.
 */
export async function drawWorldMap(
  canvas: HTMLCanvasElement,
  w: WorldSpec,
  terrain: TerrainProfile,
  field: BiomeField | null,
  marks: Vector3[],
  cancelled: () => boolean,
): Promise<MapStats | null> {
  const W = canvas.width;
  const H = canvas.height;
  const t = new TerrainHeights(terrain, w.seed, w.radius);
  const texel = (w.radius * Math.PI) / H;
  const heights = new Float32Array(W * H);
  const n = w.biomes.length;
  const weights = new Float32Array(W * H * n);
  const dir = new Vector3();
  const sample = { height: 0, slope: 0 };
  const wt: number[] = [];
  let start = performance.now();
  for (let y = 0; y < H; y++) {
    const lat = Math.PI / 2 - ((y + 0.5) / H) * Math.PI;
    for (let x = 0; x < W; x++) {
      const lon = ((x + 0.5) / W) * Math.PI * 2 - Math.PI;
      dir.set(Math.cos(lat) * Math.cos(lon), Math.sin(lat), Math.cos(lat) * Math.sin(lon));
      const i = y * W + x;
      heights[i] = t.sample(dir, texel, sample).height;
      if (field) {
        field.weights(dir, heights[i], wt);
        for (let k = 0; k < n; k++) weights[i * n + k] = wt[k];
      } else weights[i * n] = 1;
    }
    if (performance.now() - start > 12) {
      await new Promise((r) => setTimeout(r, 0));
      if (cancelled()) return null;
      start = performance.now();
    }
  }

  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(W, H);
  const scale = terrain.heightScale ?? 1;
  const range = Math.max(20, (terrain.ridgeHeight + terrain.continentHeight) / scale);
  const tint = hexToDisplay(w.groundTint).map((c) => c * w.groundBrightness) as RGB;
  const lava = w.sea === 'lava';
  const shallow = lava ? ([1, 0.5, 0.12] as RGB) : hexToDisplay(w.waterShallow);
  const deep = lava ? ([0.55, 0.12, 0.03] as RGB) : hexToDisplay(w.waterDeep);
  const looks = w.biomes.map((b) => BIOMES[b.id]);
  const snowLines = w.biomes.map((b) =>
    b.id === 'meadow'
      ? w.snow
        ? w.snowLine
        : Number.POSITIVE_INFINITY
      : (BIOMES[b.id].snowLine ?? Number.POSITIVE_INFINITY),
  );
  // Hill shading: light from the upper left, by the slope across a pixel.
  const shadeK = 1.5 / texel;
  let wet = 0;
  let area = 0;
  let peak = Number.NEGATIVE_INFINITY;
  const shares = new Array(n).fill(0);
  let landArea = 0;
  for (let y = 0; y < H; y++) {
    const cosLat = Math.cos(Math.PI / 2 - ((y + 0.5) / H) * Math.PI);
    for (let x = 0; x < W; x++) {
      const i = y * W + x;
      const h = heights[i];
      const hp = h / scale;
      let c: RGB = [0, 0, 0];
      area += cosLat;
      peak = Math.max(peak, h);
      if (h < 0) {
        wet += cosLat;
        c = mix(shallow, deep, 1 - Math.exp(hp / 25));
      } else {
        landArea += cosLat;
        for (let k = 0; k < n; k++) {
          const wk = weights[i * n + k];
          if (wk <= 0) continue;
          shares[k] += wk * cosLat;
          const m = looks[k].map;
          let b = mix(m.low, m.high, smooth(0, range * 0.6, hp));
          b = mix(m.shore, b, smooth(0, looks[k].dry * 1.5, hp));
          if (hp > snowLines[k]) b = [0.92, 0.94, 0.97];
          c = [c[0] + b[0] * wk, c[1] + b[1] * wk, c[2] + b[2] * wk];
        }
        c = [c[0] * tint[0], c[1] * tint[1], c[2] * tint[2]];
        const left = heights[y * W + ((x + W - 1) % W)];
        const up = heights[Math.max(0, y - 1) * W + x];
        const shade = 1 + Math.max(-0.45, Math.min(0.45, (h - left + (h - up)) * shadeK));
        c = [c[0] * shade, c[1] * shade, c[2] * shade];
      }
      img.data[i * 4] = to8(c[0]);
      img.data[i * 4 + 1] = to8(c[1]);
      img.data[i * 4 + 2] = to8(c[2]);
      img.data[i * 4 + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  for (const m of marks) {
    const lon = Math.atan2(m.z, m.x);
    const lat = Math.asin(Math.max(-1, Math.min(1, m.y)));
    const px = ((lon + Math.PI) / (Math.PI * 2)) * W;
    const py = ((Math.PI / 2 - lat) / Math.PI) * H;
    ctx.beginPath();
    ctx.arc(px, py, 4, 0, Math.PI * 2);
    ctx.fillStyle = '#f7c86b';
    ctx.strokeStyle = '#1a1206';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fill();
  }
  return { water: wet / area, peak, biomes: shares.map((s) => s / Math.max(landArea, 1e-9)) };
}

/** Where each world was drawn in the universe view (canvas pixels), for picking and dragging. */
export interface Drawn {
  x: number;
  y: number;
  r: number;
}

/** A world being dragged onto another: which, where the pointer is, and what's under it (−1: nothing). */
export interface Drag {
  from: number;
  x: number;
  y: number;
  over: number;
}

/**
 * The universe from outside: each world a disc (its size to scale, in its first biome's colour),
 * each cave a line between the walls it joins with its length, `selected` outlined, the start
 * world starred. Seen along the direction the worlds spread least, so they overlap least.
 * `layout` keeps the discs where they were (while dragging). Returns where each world was drawn.
 */
export function drawUniverse(
  canvas: HTMLCanvasElement,
  u: UniverseSpec,
  built: BuiltUniverse,
  selected: number,
  drag: Drag | null = null,
  layout: Drawn[] | null = null,
): Drawn[] {
  const ctx = canvas.getContext('2d')!;
  const W = canvas.width;
  const H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  const drawn = layout ?? project(u, built, W, H);

  // Caves, wall to wall.
  ctx.lineWidth = 3;
  ctx.strokeStyle = '#f7c86b';
  for (const l of built.placement.links) {
    const a = drawn[l.from];
    const b = drawn[l.to];
    const d = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const ux = (b.x - a.x) / d;
    const uy = (b.y - a.y) / d;
    ctx.beginPath();
    ctx.moveTo(a.x + ux * a.r, a.y + uy * a.r);
    ctx.lineTo(b.x - ux * b.r, b.y - uy * b.r);
    ctx.stroke();
  }
  // Worlds, the selected one last.
  const order = drawn.map((_, i) => i).sort((a, b) => (a === selected ? 1 : b === selected ? -1 : 0));
  ctx.font = '12px "Segoe UI", system-ui, sans-serif';
  ctx.textAlign = 'center';
  for (const i of order) {
    const d = drawn[i];
    const w = u.worlds[i];
    const g = ctx.createRadialGradient(d.x - d.r * 0.3, d.y - d.r * 0.3, d.r * 0.1, d.x, d.y, d.r);
    g.addColorStop(0, css(BIOMES[w.biomes[0].id].map.low));
    g.addColorStop(1, 'rgba(10, 12, 16, 0.95)');
    ctx.beginPath();
    ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
    ctx.fillStyle = g;
    ctx.fill();
    const target = drag !== null && drag.over === i && drag.from !== i;
    ctx.lineWidth = i === selected || target ? 3 : 1;
    ctx.strokeStyle = target ? '#9fe0ff' : i === selected ? '#f7c86b' : 'rgba(244, 239, 230, 0.4)';
    ctx.stroke();
    ctx.fillStyle = i === selected ? '#f7c86b' : 'rgba(244, 239, 230, 0.85)';
    ctx.fillText(`${i === u.start ? '★ ' : ''}${w.name}`, d.x, Math.min(H - 4, d.y + d.r + 14));
  }
  // Each cave's length, on its line.
  ctx.font = '11px "Segoe UI", system-ui, sans-serif';
  for (const l of built.placement.links) {
    const a = drawn[l.from];
    const b = drawn[l.to];
    const label = formatDistance(l.gap);
    const tw = ctx.measureText(label).width + 8;
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    ctx.fillStyle = 'rgba(14, 18, 24, 0.85)';
    ctx.fillRect(mx - tw / 2, my - 8, tw, 15);
    ctx.fillStyle = '#f7c86b';
    ctx.fillText(label, mx, my + 3);
  }
  if (drag) {
    const a = drawn[drag.from];
    ctx.setLineDash([6, 5]);
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#9fe0ff';
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(drag.x, drag.y);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  return drawn;
}

/** Where each world goes in the view: projected onto the plane the worlds spread over most, fitted to the canvas. */
function project(u: UniverseSpec, built: BuiltUniverse, W: number, H: number): Drawn[] {
  const centers = built.placement.centers;
  const mean = new Vector3();
  for (const c of centers) mean.add(c);
  mean.divideScalar(centers.length);
  const rel = centers.map((c) => c.clone().sub(mean));
  const [ex, ey] = planeOf(rel);
  const pts = rel.map((p) => ({ x: p.dot(ex), y: p.dot(ey) }));
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  pts.forEach((p, i) => {
    const r = u.worlds[i].radius;
    minX = Math.min(minX, p.x - r);
    maxX = Math.max(maxX, p.x + r);
    minY = Math.min(minY, p.y - r);
    maxY = Math.max(maxY, p.y + r);
  });
  const pad = 26;
  const s = Math.min((W - 2 * pad) / (maxX - minX), (H - 2 * pad) / (maxY - minY));
  const ox = W / 2 - ((minX + maxX) / 2) * s;
  const oy = H / 2 + ((minY + maxY) / 2) * s;
  return pts.map((p, i) => ({ x: ox + p.x * s, y: oy - p.y * s, r: Math.max(6, u.worlds[i].radius * s) }));
}

/** Two axes of the plane the points spread over most (the view looks along the third). */
function planeOf(points: Vector3[]): [Vector3, Vector3] {
  const m = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  for (const p of points) {
    const v = [p.x, p.y, p.z];
    for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) m[r * 3 + c] += v[r] * v[c];
  }
  const apply = (v: Vector3) =>
    new Vector3(
      m[0] * v.x + m[1] * v.y + m[2] * v.z,
      m[3] * v.x + m[4] * v.y + m[5] * v.z,
      m[6] * v.x + m[7] * v.y + m[8] * v.z,
    );
  const power = (start: Vector3, against?: Vector3) => {
    let v = start.clone().normalize();
    for (let i = 0; i < 40; i++) {
      const next = apply(v);
      if (against) next.addScaledVector(against, -next.dot(against));
      if (next.lengthSq() < 1e-12) break;
      v = next.normalize();
    }
    return v;
  };
  const e1 = power(new Vector3(0.6, 0.3, 0.74));
  let e2 = power(new Vector3(-0.3, 0.9, 0.2).addScaledVector(e1, -0.1), e1);
  e2.addScaledVector(e1, -e2.dot(e1));
  if (e2.lengthSq() < 1e-9) {
    const helper = Math.abs(e1.y) < 0.9 ? new Vector3(0, 1, 0) : new Vector3(1, 0, 0);
    e2 = helper.cross(e1);
  }
  return [e1, e2.normalize()];
}

/** "850 m", "3.2 km", "120 km". */
export function formatDistance(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`;
  if (m < 10_000) return `${(m / 1000).toFixed(1)} km`;
  return `${Math.round(m / 1000)} km`;
}

function css(c: RGB): string {
  return `rgb(${to8(c[0])}, ${to8(c[1])}, ${to8(c[2])})`;
}

function hexToDisplay(hex: string): RGB {
  const n = Number.parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function mix(a: RGB, b: RGB, t: number): RGB {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

function smooth(e0: number, e1: number, x: number): number {
  const t = Math.min(Math.max((x - e0) / (e1 - e0), 0), 1);
  return t * t * (3 - 2 * t);
}

function to8(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v * 255)));
}
